import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const MODULES = [
  {
    id: '01',
    name: 'Daily Motivation Brief',
    tag: 'Motivation',
    desc: 'Every morning begins with focused mental conditioning. The system compiles high-impact motivational content into a single curated video designed to reinforce discipline, focus, and momentum. Built to prime mindset before execution begins.',
  },
  {
    id: '02',
    name: 'Athlete Management Intelligence',
    tag: 'Athletes',
    desc: 'Monitor and manage athlete relationships with precision. The dashboard tracks performance data and automatically prepares personalized communication drafts — check-ins, motivation messages, progress recognition, and follow-up prompts. Communication stays consistent without manual effort.',
  },
  {
    id: '03',
    name: 'Fashion Intelligence Feed',
    tag: 'Style',
    desc: 'Style data curated by AI. The system scans and ranks top-performing fashion content, identifying the strongest looks and latest trends aligned with Rey\'s aesthetic preferences. Relevant inspiration. Zero wasted scrolling.',
  },
  {
    id: '04',
    name: 'Performance Training System',
    tag: 'Training',
    desc: 'Daily training programmed intelligently. Every workout is generated based on schedule, recovery, and performance goals — warm-up, main training block, sets & reps, coaching notes, and cool-down. Each exercise includes a tap-to-play video demonstration so form is never a guess.',
  },
  {
    id: '05',
    name: 'Nutrition Intelligence',
    tag: 'Nutrition',
    desc: 'Food planning optimized for performance. The dashboard generates daily meal structures aligned with body composition and energy requirements. Available in Lean, Performance, and Comfort modes — every plan includes calorie and macro breakdowns for precise execution.',
  },
  {
    id: '06',
    name: 'Wardrobe Command Center',
    tag: 'Wardrobe',
    desc: 'Eliminate unnecessary style decisions. The system builds complete suit combinations for Thursday through Saturday — each shown with a full look photo and the individual pieces. Designed for variety, cohesion, and confidence. Look intentional without overthinking.',
  },
  {
    id: '07',
    name: 'Real Estate Deal Intelligence',
    tag: 'Real Estate',
    desc: 'Investment opportunities ranked automatically. The dashboard scans target markets and scores properties on cash flow, rental demand, market strength, and ROI — and you can search any other city, state, or ZIP on demand. Every property receives a clear recommendation: Strong Buy / Watch / Pass.',
  },
  {
    id: '08',
    name: 'Income & Budget',
    tag: 'Finance',
    desc: 'A private, on-device command center for money. Log and document income, build a monthly budget across categories, and track spending against targets in real time — with a one-tap privacy mode to hide amounts. Data stays securely on your device.',
  },
];

const STATS = [
  { value: '8',     label: 'AI Modules'                  },
  { value: '24/7',  label: 'Automated Processing'        },
  { value: '5:45',  label: 'Executive Brief Ready'       },
  { value: '100%',  label: 'Personalized for Rey Daniel' },
];

const AI_EXAMPLES = [
  '"Show today\'s best property opportunity."',
  '"Add a new athlete."',
  '"Adjust calorie target to 2500."',
  '"Show Saturday\'s outfit."',
  '"Search deals in Tampa, FL."',
  '"Log $2,500 of consulting income."',
];

export default function Guide() {
  const navigate = useNavigate();
  const [hoveredId, setHoveredId] = useState(null);

  function openChat() {
    // Trigger the floating chat bubble
    const btn = document.querySelector('[data-chat-trigger]');
    if (btn) btn.click();
    else navigate('/dashboard');
  }

  return (
    <div className="min-h-screen bg-black text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>

      {/* ── Nav ──────────────────────────────────────────────────── */}
      <nav className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 py-4 border-b border-white/8 bg-black/90 backdrop-blur-sm">
        <div className="flex items-center gap-3">
          <img src="/rdl-logo.png" alt="RDL" className="h-10 w-auto" />
          <span className="text-[10px] tracking-[0.3em] uppercase text-gray-500 hidden sm:block">Executive Dashboard</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-[9px] tracking-[0.25em] uppercase border px-2.5 py-1"
            style={{ borderColor: 'var(--gold)', color: 'var(--gold)' }}>
            Beta
          </span>
          <button
            onClick={() => navigate('/dashboard')}
            className="text-[9px] tracking-[0.25em] uppercase border border-white/20 px-4 py-2 hover:border-white/60 transition-colors"
          >
            Enter Dashboard
          </button>
        </div>
      </nav>

      {/* ── Hero ─────────────────────────────────────────────────── */}
      <section className="min-h-screen flex flex-col items-center justify-center px-6 pt-24 pb-16 text-center">
        <img src="/rdl-logo.png" alt="RDL" className="h-20 w-auto mb-8 opacity-95" />

        <div className="mb-5">
          <span className="text-[9px] tracking-[0.4em] uppercase border px-3 py-1.5"
            style={{ borderColor: 'var(--gold)', color: 'var(--gold)' }}>
            Beta Version — Private Access
          </span>
        </div>

        <p className="text-[10px] tracking-[0.35em] uppercase text-gray-500 mb-4">
          RDI Executive Dashboard
        </p>

        <h1 className="text-4xl sm:text-5xl md:text-6xl font-light tracking-[0.06em] uppercase leading-tight max-w-3xl mb-6">
          Rey Daniel's Personal<br />
          <span className="font-semibold">AI Operating System</span>
        </h1>

        <p className="text-sm text-gray-400 max-w-xl leading-relaxed mb-3">
          A private intelligence platform engineered to optimize daily performance, decision-making, and execution across every critical area of life.
        </p>
        <p className="text-sm text-gray-400 max-w-xl leading-relaxed mb-3">
          Eight AI-powered modules run automatically overnight — delivering a fully prepared executive brief before the day begins.
        </p>
        <p className="text-[11px] tracking-[0.15em] uppercase text-gray-500 mb-10">
          Built for speed. Built for discipline. Built for results.
        </p>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => navigate('/dashboard')}
            className="px-8 py-3.5 text-[10px] tracking-[0.3em] uppercase font-semibold transition-opacity hover:opacity-80"
            style={{ backgroundColor: 'var(--gold)', color: '#000' }}
          >
            Enter Dashboard
          </button>
          <a href="#intelligence"
            className="px-8 py-3.5 text-[10px] tracking-[0.3em] uppercase border border-white/20 hover:border-white/60 transition-colors text-center">
            View Intelligence
          </a>
        </div>
      </section>

      {/* ── Stats Bar ────────────────────────────────────────────── */}
      <section className="border-t border-b border-white/8">
        <div className="max-w-3xl mx-auto">
          <p className="text-center text-[9px] tracking-[0.35em] uppercase text-gray-600 py-5 border-b border-white/8">
            Daily Intelligence, Fully Automated
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/8">
            {STATS.map(({ value, label }) => (
              <div key={label} className="py-8 px-4 text-center bg-black">
                <p className="text-2xl sm:text-3xl font-light tracking-wide" style={{ color: 'var(--gold)' }}>{value}</p>
                <p className="text-[9px] tracking-[0.2em] uppercase text-gray-600 mt-2">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Overview ─────────────────────────────────────────────── */}
      <section className="px-6 py-20 max-w-3xl mx-auto border-b border-white/8">
        <p className="text-[9px] tracking-[0.4em] uppercase mb-5" style={{ color: 'var(--gold)' }}>
          Overview
        </p>
        <h2 className="text-2xl sm:text-3xl font-light tracking-[0.08em] uppercase leading-snug mb-8">
          Your Executive<br />
          <span className="font-semibold">Command Center</span>
        </h2>
        <div className="space-y-4 text-sm text-gray-400 leading-relaxed">
          <p>
            The <span className="text-white font-medium">RDI Executive Dashboard</span> is Rey Daniel's private AI operating system — designed to eliminate friction, reduce decision fatigue, and create daily operational clarity.
          </p>
          <p>
            Every night, a secure automated pipeline collects live data, analyzes trends, ranks opportunities, and prepares a complete personalized dashboard. By morning, every critical decision support system is already updated.
          </p>
          <div className="border-l-2 pl-5 py-2 space-y-2 my-6" style={{ borderColor: 'var(--gold)' }}>
            {['No wasted motion.', 'No unnecessary searching.', 'No manual organization.', 'Just execution.'].map(line => (
              <p key={line} className="text-[11px] tracking-[0.15em] uppercase text-gray-500">{line}</p>
            ))}
          </div>
          <p>
            Each day begins with actionable intelligence across motivation, athlete management, style, training, nutrition, wardrobe planning, real estate investments, and private income & budget tracking.
          </p>
        </div>
      </section>

      {/* ── Intelligence Modules ─────────────────────────────────── */}
      <section id="intelligence" className="px-6 py-20 border-b border-white/8">
        <div className="max-w-3xl mx-auto">
          <p className="text-[9px] tracking-[0.4em] uppercase mb-5" style={{ color: 'var(--gold)' }}>
            Intelligence Modules
          </p>
          <h2 className="text-2xl sm:text-3xl font-light tracking-[0.08em] uppercase mb-12">
            Eight Core<br />
            <span className="font-semibold">Intelligence Modules</span>
          </h2>

          <div>
            {MODULES.map((mod) => (
              <div
                key={mod.id}
                onMouseEnter={() => setHoveredId(mod.id)}
                onMouseLeave={() => setHoveredId(null)}
                className="border-b border-white/8 py-7 group"
              >
                <div className="flex items-start gap-5">
                  <span className="text-[10px] font-light w-6 shrink-0 mt-0.5 transition-colors"
                    style={{ color: hoveredId === mod.id ? 'var(--gold)' : '#444' }}>
                    {mod.id}
                  </span>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <p className="text-sm font-medium tracking-[0.1em] uppercase">{mod.name}</p>
                      <span className="text-[8px] tracking-[0.2em] uppercase border px-1.5 py-0.5 shrink-0 transition-colors"
                        style={hoveredId === mod.id
                          ? { borderColor: 'var(--gold)', color: 'var(--gold)' }
                          : { borderColor: '#2a2a2a', color: '#555' }}>
                        {mod.tag}
                      </span>
                    </div>
                    <p className="text-[12px] text-gray-500 leading-relaxed">{mod.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── AI Assistant ─────────────────────────────────────────── */}
      <section className="px-6 py-20 border-b border-white/8">
        <div className="max-w-3xl mx-auto">
          <p className="text-[9px] tracking-[0.4em] uppercase mb-5" style={{ color: 'var(--gold)' }}>
            AI Assistant
          </p>
          <h2 className="text-2xl sm:text-3xl font-light tracking-[0.08em] uppercase mb-4">
            Executive<br />
            <span className="font-semibold">AI Assistant</span>
          </h2>
          <p className="text-sm text-gray-400 leading-relaxed mb-3">
            A built-in AI assistant provides direct control over the entire operating system.
          </p>
          <p className="text-[11px] tracking-[0.15em] uppercase text-gray-600 mb-8">
            Interact naturally using simple commands.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-8">
            {AI_EXAMPLES.map((q) => (
              <div key={q} className="border border-white/8 px-4 py-3 hover:border-white/20 transition-colors">
                <p className="text-[11px] text-gray-500 italic leading-relaxed">{q}</p>
              </div>
            ))}
          </div>

          <div className="flex gap-4">
            {['Fast.', 'Conversational.', 'Intelligent.'].map(word => (
              <span key={word} className="text-[10px] tracking-[0.25em] uppercase" style={{ color: 'var(--gold)' }}>
                {word}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why It Exists ────────────────────────────────────────── */}
      <section className="px-6 py-20 border-b border-white/8">
        <div className="max-w-3xl mx-auto">
          <p className="text-[9px] tracking-[0.4em] uppercase mb-5" style={{ color: 'var(--gold)' }}>
            Why It Exists
          </p>
          <h2 className="text-2xl sm:text-3xl font-light tracking-[0.08em] uppercase mb-8">
            Designed For<br />
            <span className="font-semibold">Elite Execution</span>
          </h2>
          <div className="space-y-4 text-sm text-gray-400 leading-relaxed">
            <p>
              The RDI Executive Dashboard exists to ensure Rey Daniel operates with maximum clarity, speed, and discipline.
            </p>
            <p>High-level performance requires systems.</p>
            <p>
              This platform removes repetitive decisions and automates intelligence gathering so focus stays on what matters most — <span className="text-white font-medium">execution.</span>
            </p>
            <p className="text-[11px] tracking-[0.2em] uppercase text-gray-600 pt-2">
              When systems improve, outcomes improve.
            </p>
          </div>
        </div>
      </section>

      {/* ── Final CTA ────────────────────────────────────────────── */}
      <section className="px-6 py-24 text-center">
        <p className="text-[9px] tracking-[0.4em] uppercase text-gray-600 mb-6">Live Now</p>
        <h2 className="text-3xl sm:text-4xl font-light tracking-[0.08em] uppercase mb-4">
          Begin the Day With<br />
          <span className="font-semibold">Complete Clarity</span>
        </h2>
        <div className="space-y-2 text-sm text-gray-500 mb-10">
          <p>Every morning, the intelligence is ready.</p>
          <p>The decisions are simplified.</p>
          <p>The path forward is clear.</p>
          <p className="text-[10px] tracking-[0.2em] uppercase pt-2" style={{ color: 'var(--gold)' }}>
            Welcome to the RDI Executive Dashboard.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => navigate('/dashboard')}
            className="px-10 py-4 text-[10px] tracking-[0.35em] uppercase font-semibold transition-opacity hover:opacity-80"
            style={{ backgroundColor: 'var(--gold)', color: '#000' }}
          >
            Enter Dashboard
          </button>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-10 py-4 text-[10px] tracking-[0.35em] uppercase border border-white/20 hover:border-white/60 transition-colors"
          >
            Open AI Assistant
          </button>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────────── */}
      <footer className="px-6 py-8 border-t border-white/8">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <img src="/rdl-logo.png" alt="RDL" className="h-8 w-auto opacity-25" />
          <div className="text-right">
            <p className="text-[9px] tracking-[0.2em] uppercase text-gray-700">RDI Executive Dashboard</p>
            <span className="text-[9px] tracking-[0.15em] uppercase" style={{ color: 'var(--gold-dim)' }}>Beta</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
